import { Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ModulosService } from './modulos.service';
import { BackendMenuItem } from '../shared/models/menu-item.model';
import { AppNavItem, mapBackendMenuToNav } from './menu-mapping';

export type NavStatus = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * Mantiene el menú lateral en sync con los permisos del usuario.
 * Expone los items via BehaviorSubject + un signal de estado para que
 * el side-navigation pueda mostrar loading/error/retry.
 *
 * i18n: el backend devuelve `Modulo.Descripcion` en español; el mapper traduce
 * usando los namespaces `menu.modulos.*` (por controller del legacy URL) y
 * `menu.grupos.*` (por slug del texto). Cacheamos el response crudo para
 * re-mapear cuando cambia el idioma sin tener que refetchear.
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
  private modulos = inject(ModulosService);
  private translate = inject(TranslateService);

  private _items$ = new BehaviorSubject<AppNavItem[]>([]);
  readonly items$: Observable<AppNavItem[]> = this._items$.asObservable();

  /** Estado del último intento de carga (idle | loading | loaded | error). */
  status = signal<NavStatus>('idle');

  /** Cache del response crudo — necesario para re-mapear en `onLangChange`
   *  sin hacer otro fetch al backend. Se llena en `load()` y se limpia en `clear()`. */
  private rawBackend: BackendMenuItem[] = [];

  constructor() {
    // Re-traducir el menu cuando cambia el idioma. No re-fetch — usamos la
    // cache del response anterior. Si nunca se cargo, no hay nada que hacer.
    this.translate.onLangChange.subscribe(() => {
      if (this.rawBackend.length > 0) {
        this._items$.next(filtrarChatDelSidebar(mapBackendMenuToNav(this.rawBackend, this.translate)));
      }
    });
  }

  get currentItems(): AppNavItem[] {
    return this._items$.value;
  }

  async load(): Promise<void> {
    this.status.set('loading');
    try {
      const backend = await firstValueFrom(this.modulos.getMenu());
      this.rawBackend = backend;
      const nav = filtrarChatDelSidebar(mapBackendMenuToNav(backend, this.translate));
      this._items$.next(nav);
      this.status.set('loaded');
      if (nav.length === 0) {
        console.warn('NavigationService: /api/modulos/menu devolvio array vacio. ' +
          'El usuario no tiene modulos asignados o el backend filtro todo por permisos.');
      }
    } catch (err) {
      console.error('NavigationService: fallo al cargar /api/modulos/menu', err);
      this.rawBackend = [];
      this._items$.next([]);
      this.status.set('error');
    }
  }

  async ensureLoaded(): Promise<void> {
    if (this.status() === 'idle' || this.status() === 'error') {
      await this.load();
    }
  }

  /** Reintenta — usado por el botón "Reintentar" en el sidebar. */
  retry(): Promise<void> { return this.load(); }

  clear(): void {
    this.rawBackend = [];
    this._items$.next([]);
    this.status.set('idle');
  }
}

/**
 * Saca el item "Chat" del sidebar principal. El módulo Chat sigue existiendo
 * y accesible vía el icono del header / botón inferior del sidebar — solo lo
 * removemos del listado de módulos para no duplicarlo.
 *
 * Filtra recursivamente porque un item puede estar adentro de un grupo
 * (ej. dentro de "Comunicación"). Se borran tanto el match exacto del path
 * `/chat` como cualquier alias razonable.
 */
function filtrarChatDelSidebar(items: AppNavItem[]): AppNavItem[] {
  const esChat = (it: AppNavItem): boolean => {
    const p = (it.path ?? '').toLowerCase();
    return p === '/chat' || p === 'chat/index' || p === '/chat/index';
  };
  return items
    .filter(it => !esChat(it))
    .map(it => it.items?.length
      ? { ...it, items: filtrarChatDelSidebar(it.items) }
      : it);
}
